import React from 'react';
import { Plus, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import CustomInput from '../../../components/CustomInput/CustomInput';
import {
  CrawlRule,
  CrawlRuleScope,
  EXCLUSION_SCOPE_LABELS,
  EXCLUSION_SCOPE_PLACEHOLDERS
} from '../../../types/WebsiteSource';

export interface EditableExclusion extends CrawlRule {
  key: number;
}

interface ExclusionEditorProps {
  exclusions: EditableExclusion[];
  onChange: (exclusions: EditableExclusion[]) => void;
  disabled?: boolean;
}

const SCOPES: CrawlRuleScope[] = ['PATH_PREFIX', 'EXACT_PAGE'];

let nextExclusionKey = 0;

export const toEditableExclusion = (rule: CrawlRule): EditableExclusion => {
  nextExclusionKey += 1;
  return { ...rule, key: nextExclusionKey };
};

export const emptyExclusion = (): EditableExclusion =>
  toEditableExclusion({ scope: 'PATH_PREFIX', value: '' });

function ExclusionEditor({
  exclusions,
  onChange,
  disabled
}: Readonly<ExclusionEditorProps>): React.ReactElement {
  const updateExclusion = (index: number, patch: Partial<CrawlRule>): void => {
    onChange(
      exclusions.map((exclusion, i) =>
        i === index ? { ...exclusion, ...patch } : exclusion
      )
    );
  };

  const removeExclusion = (index: number): void => {
    onChange(exclusions.filter((_, i) => i !== index));
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>Excluded pages</span>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={disabled}
          onClick={() => onChange([...exclusions, emptyExclusion()])}
        >
          <Plus className='mr-1 h-4 w-4' /> Add exclusion
        </Button>
      </div>
      <p className='text-xs text-muted-foreground'>
        Every page under the website address is crawled except the ones listed
        here. Paths are relative to the website address.
      </p>
      {exclusions.length === 0 && (
        <p className='text-sm text-muted-foreground'>No pages are excluded.</p>
      )}
      {exclusions.map((exclusion, index) => (
        <div
          key={exclusion.key}
          className='flex flex-wrap items-start gap-2'
          data-testid={`exclusion-row-${index}`}
        >
          <Select
            value={exclusion.scope}
            disabled={disabled}
            onValueChange={(value) =>
              updateExclusion(index, { scope: value as CrawlRuleScope })
            }
          >
            <SelectTrigger className='w-[240px]' aria-label='Exclusion type'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((scope) => (
                <SelectItem key={scope} value={scope}>
                  {EXCLUSION_SCOPE_LABELS[scope]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className='min-w-[240px] flex-1'>
            <CustomInput
              value={exclusion.value}
              placeholder={EXCLUSION_SCOPE_PLACEHOLDERS[exclusion.scope]}
              disabled={disabled}
              onChange={(e) =>
                updateExclusion(index, { value: e.target.value })
              }
              fullWidth
            />
          </div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            disabled={disabled}
            aria-label={`Remove exclusion ${index + 1}`}
            onClick={() => removeExclusion(index)}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
    </div>
  );
}

export default ExclusionEditor;
